import { useMemo, useState } from "react";
import { formatRentangTanggal } from "@/lib/datetime";
import { BLOK_JAGA, BLOK_STASE_LUAR, BLOK_STASE_RSCM, LABEL_BLOK, hitungKuota } from "@/lib/rotasiCepat";
import {
    NAMA_BULAN, batasBulanIso, cekRentang, formKosong, geserBulan, jagaAdaDi, pilihRentang, tambahStase, toggleJaga,
} from "@/lib/kalenderRotasi";
import KalenderBulan from "@/components/KalenderBulan";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus } from "lucide-react";
import { toast } from "sonner";

// Isian kalender Rotasi Klinik yang DIPAKAI BERSAMA panel Input Rotasi Cepat (1 peserta) & dialog Isi Massal
// (banyak peserta): mode Stase RSCM/Stase Luar/Jaga, kalender, tempat, klik tanggal, "Tambahkan ke daftar".
// Aturan sebenarnya ditegakkan server; di sini hanya pemeriksaan awal (kuota, lintas bulan) supaya cepat.

const KOSONG = "__none__";
const MODE = [BLOK_STASE_RSCM, BLOK_STASE_LUAR, BLOK_JAGA];
const TEMPAT_KOSONG = { departemen_id: "", divisi_id: "", instalasi_id: "", nama_rs: "" };
const hariIniWib = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Jakarta" });

function PilihMaster({ value, onChange, opsi, placeholder, disabled, label }) {
    return (
        <Select value={value || KOSONG} onValueChange={(v) => onChange(v === KOSONG ? "" : v)} disabled={disabled}>
            <SelectTrigger className="h-8 text-xs" aria-label={label}>
                <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
                <SelectItem value={KOSONG}>{placeholder}</SelectItem>
                {opsi.map((o) => <SelectItem key={o.id} value={o.id}>{o.nama}</SelectItem>)}
            </SelectContent>
        </Select>
    );
}

function PilihTempat({ mode, tempat, master, onUbah }) {
    if (mode === BLOK_STASE_LUAR) {
        return (
            <Input
                value={tempat.nama_rs} list="rc-rs-saran" maxLength={200} onChange={(e) => onUbah({ nama_rs: e.target.value })}
                placeholder="Nama RS, mis. RSUD Pasar Rebo" className="h-8 text-xs" aria-label="Stase Luar: Nama RS"
                data-testid="rc-nama-rs"
            />
        );
    }
    const label = LABEL_BLOK[mode];
    const divisi = master.divisi.filter((d) => d.departemen_id === tempat.departemen_id);
    return (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
            <PilihMaster value={tempat.departemen_id} opsi={master.departemen} placeholder="Pilih departemen"
                label={`${label}: Departemen`} onChange={(v) => onUbah({ departemen_id: v, divisi_id: "" })} />
            <PilihMaster value={tempat.divisi_id} opsi={divisi} disabled={!tempat.departemen_id || divisi.length === 0}
                placeholder={tempat.departemen_id ? "Divisi (opsional)" : "Pilih departemen dulu"}
                label={`${label}: Divisi`} onChange={(v) => onUbah({ divisi_id: v })} />
            <PilihMaster value={tempat.instalasi_id} opsi={master.instalasi}
                placeholder={mode === BLOK_JAGA ? "Pilih instalasi" : "Instalasi (opsional)"}
                label={`${label}: Instalasi`} onChange={(v) => onUbah({ instalasi_id: v })} />
        </div>
    );
}

// State isian: form ("Akan disimpan"), mode, bulan yang dilihat, rentang terpilih, tempat. `terpakai` = kuota yang
// sudah dipakai (panel: data tersimpan peserta; Isi Massal: 0, kuota tiap peserta dinilai server di pratinjau).
// onUbah dipanggil setiap daftar berubah (mis. menghapus galat/pratinjau lama).
export function useIsianKalender({ bulan, tahun, terpakai, maks, aturan, onUbah }) {
    const bln = Number(bulan);
    const thn = Number(tahun);
    const [form, setForm] = useState(formKosong);
    const [mode, setMode] = useState(BLOK_STASE_RSCM);
    const [lihat, setLihat] = useState({ bulan: bln, tahun: thn });
    const [pilihan, setPilihan] = useState(null);
    const [tempat, setTempat] = useState(TEMPAT_KOSONG);
    const kuota = useMemo(() => (terpakai && maks ? hitungKuota(form, terpakai, maks) : null), [form, terpakai, maks]);

    const ubahForm = (f) => { setForm(f); onUbah?.(); };
    // Kembali ke keadaan awal (dialog Isi Massal yg ditutup/dibuka lagi mulai bersih).
    const reset = () => {
        setForm(formKosong()); setMode(BLOK_STASE_RSCM); setLihat({ bulan: bln, tahun: thn }); setPilihan(null); setTempat(TEMPAT_KOSONG);
    };

    const klikHari = (d) => {
        if (mode !== BLOK_JAGA) {
            setPilihan((p) => pilihRentang(p, d));
            return;
        }
        if (!tempat.departemen_id || !tempat.instalasi_id) {
            toast.error("Pilih Departemen dan Instalasi Jaga dulu");
            return;
        }
        if (!jagaAdaDi(form, d, tempat) && !kuota.jaga.bisaTambah) {
            toast.error(`Kuota jaga penuh (${kuota.jaga.maks}/${kuota.jaga.maks})`);
            return;
        }
        ubahForm(toggleJaga(form, d, tempat));
    };

    const tambahkan = () => {
        if (!pilihan?.awal) return toast.error("Klik tanggal awal lalu tanggal akhir di kalender");
        if (mode === BLOK_STASE_RSCM && !tempat.departemen_id && !tempat.instalasi_id) {
            return toast.error("Pilih Departemen atau Instalasi");
        }
        if (mode === BLOK_STASE_LUAR && !tempat.nama_rs.trim()) return toast.error("Nama RS wajib diisi");
        if (!kuota.stase.bisaTambah) return toast.error(`Kuota stase penuh (${kuota.stase.maks}/${kuota.stase.maks})`);
        const pesan = cekRentang(pilihan.awal, pilihan.akhir || pilihan.awal, bln, thn, aturan?.batas_lintas_bulan,
            aturan?.pesan_lintas_bulan);
        if (pesan) return toast.error(pesan);
        ubahForm(tambahStase(form, mode, pilihan, tempat));
        setPilihan(null);
        return null;
    };

    return {
        bln, thn, form, mode, lihat, pilihan, tempat, kuota, klikHari, tambahkan, reset,
        gantiMode: (m) => { setMode(m); setPilihan(null); },
        geser: (n) => setLihat((l) => geserBulan(l.bulan, l.tahun, n)),
        ubahTempat: (u) => setTempat((t) => ({ ...t, ...u })),
        hapus: (blok, key) => ubahForm({ ...form, [blok]: form[blok].filter((b) => b.key !== key) }),
    };
}

// Tampilan isian: tombol mode, kalender, tempat, rentang/"Tambahkan ke daftar". `tanda` = hasil tandaKalender;
// `children` = penghitung/keterangan tambahan dari pemakai (panel vs Isi Massal).
export function IsianKalenderRotasi({ isian, master, tanda, children }) {
    const { bln, thn, mode, lihat, pilihan, tempat, form } = isian;
    const [awalBulan, akhirBulan] = batasBulanIso(bln, thn);
    const namaBulan = `${NAMA_BULAN[bln - 1]} ${thn}`;
    const rentangDipilih = pilihan?.awal ? formatRentangTanggal(pilihan.awal, pilihan.akhir || pilihan.awal) : null;
    return (
        <>
            <div className="flex flex-wrap gap-1" role="group" aria-label="Jenis rotasi yang diisi">
                {MODE.map((m) => (
                    <Button
                        key={m} type="button" size="sm" variant={mode === m ? "default" : "outline"} aria-pressed={mode === m}
                        className={`h-7 px-2.5 text-xs ${mode === m ? "bg-[#267B84] hover:bg-[#267B84] text-white" : ""}`}
                        onClick={() => isian.gantiMode(m)} data-testid={`rc-mode-${m}`}
                    >
                        {LABEL_BLOK[m]}
                    </Button>
                ))}
            </div>
            <div className="flex flex-wrap gap-3 items-start">
                <KalenderBulan
                    tahun={lihat.tahun} bulan={lihat.bulan} tanda={tanda} hariIni={hariIniWib()}
                    pilihan={mode === BLOK_JAGA ? null : pilihan}
                    hariAktif={(d) => mode !== BLOK_JAGA || (d >= awalBulan && d <= akhirBulan)}
                    ditekan={mode === BLOK_JAGA ? (d) => jagaAdaDi(form, d, tempat) : undefined}
                    onKlik={isian.klikHari} onGeser={isian.geser}
                />
                <div className="flex-1 min-w-[260px] space-y-2">
                    <div className="text-xs font-semibold text-slate-700">Tempat {LABEL_BLOK[mode]}</div>
                    <PilihTempat mode={mode} tempat={tempat} master={master} onUbah={isian.ubahTempat} />
                    {mode === BLOK_JAGA ? (
                        <p className="text-[11px] text-slate-500">
                            Klik tanggal di {namaBulan} untuk menyalakan/mematikan Jaga (1 tanggal = 1 jaga).
                            Departemen dan Instalasi wajib dipilih dulu.
                        </p>
                    ) : (
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs text-slate-600" data-testid="rc-rentang-dipilih">
                                {rentangDipilih ? `Rentang: ${rentangDipilih}` : "Klik tanggal awal lalu tanggal akhir."}
                            </span>
                            <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={isian.tambahkan}
                                disabled={!pilihan?.awal} data-testid="rc-tambahkan">
                                <Plus className="w-3.5 h-3.5 mr-1" /> Tambahkan ke daftar
                            </Button>
                        </div>
                    )}
                    {children}
                </div>
            </div>
        </>
    );
}
